import React, { useState, useEffect, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
	createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { canStartOutboundAttendance } from "../../utils/connections";

const filter = createFilterOptions({
	trim: true,
});

const ACTIVE_WHATSAPP_STATUSES = ["CONNECTED", "connected"];

const useStyles = makeStyles(() => ({
	actionButton: {
		borderRadius: 999,
		boxShadow: "none",
		textTransform: "none",
		"&:hover": {
			boxShadow: "none",
		},
	},
}));

const NewTicketModal = ({ modalOpen, onClose, initialContact = null }) => {
	const history = useHistory();
	const classes = useStyles();

	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingWhatsapps, setLoadingWhatsapps] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContact, setSelectedContact] = useState(null);
	const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
	const [selectedQueue, setSelectedQueue] = useState("");
	const [selectedUser, setSelectedUser] = useState("");
	const [queueUsers, setQueueUsers] = useState([]);
	const [loadingQueueUsers, setLoadingQueueUsers] = useState(false);
	const [whatsapps, setWhatsapps] = useState([]);
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const { user } = useContext(AuthContext);
	const availableQueues = Array.isArray(user?.queues) ? user.queues : [];
	const queueScopedWhatsapps = useMemo(() => selectedQueue
		? whatsapps.filter(whatsapp =>
				Array.isArray(whatsapp.queues) &&
				whatsapp.queues.some(queue => Number(queue.id) === Number(selectedQueue))
		  )
		: [], [selectedQueue, whatsapps]);
	const queueDefaultWhatsapp = selectedQueue
		? queueScopedWhatsapps.find(whatsapp => Boolean(whatsapp.isDefault)) ||
		  (queueScopedWhatsapps.length ? queueScopedWhatsapps[0] : null)
		: null;
	const filteredWhatsapps = queueScopedWhatsapps.length ? queueScopedWhatsapps : whatsapps;
	const selectedQueueUser = queueUsers.find(
		queueUser => String(queueUser.id) === String(selectedUser)
	);

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return;
		}

		if (initialContact?.id) {
			setLoading(false);
			return;
		}

		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam },
					});
					setOptions(data.contacts);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen, initialContact]);

	useEffect(() => {
		if (!modalOpen) {
			return;
		}

		if (initialContact?.id) {
			setSelectedContact(initialContact);
		}
	}, [modalOpen, initialContact]);

	useEffect(() => {
		if (!modalOpen || !selectedQueue) {
			setWhatsapps([]);
			setSelectedWhatsapp("");
			return;
		}
		let cancelled = false;

		const fetchWhatsapps = async () => {
			setLoadingWhatsapps(true);
			try {
				const { data } = await api.get("/whatsapp/", {
					params: { manualOutbound: true, queueId: selectedQueue },
				});
				const activeWhatsapps = data.filter(whatsapp =>
					ACTIVE_WHATSAPP_STATUSES.includes(String(whatsapp.status)) &&
					canStartOutboundAttendance(whatsapp)
				);

				if (cancelled) return;
				setWhatsapps(activeWhatsapps);

				const userDefaultWhatsapp = activeWhatsapps.find(
					whatsapp => Number(whatsapp.id) === Number(user?.whatsappId)
				);

				const defaultWhatsapp =
					userDefaultWhatsapp ||
					activeWhatsapps.find(whatsapp => whatsapp.isDefault) ||
					(activeWhatsapps.length === 1 ? activeWhatsapps[0] : null);

				setSelectedWhatsapp(defaultWhatsapp ? defaultWhatsapp.id : "");
			} catch (err) {
				if (cancelled) return;
				setWhatsapps([]);
				setSelectedWhatsapp("");
				toastError(err);
			} finally {
				if (!cancelled) setLoadingWhatsapps(false);
			}
		};

		fetchWhatsapps();
		return () => {
			cancelled = true;
		};
	}, [modalOpen, selectedQueue, user?.whatsappId]);

	useEffect(() => {
		if (!modalOpen) {
			return;
		}

		setSelectedUser("");
		setQueueUsers([]);
		if (availableQueues.length === 1) {
			setSelectedQueue(String(availableQueues[0].id));
		} else {
			setSelectedQueue("");
		}
	}, [modalOpen, availableQueues]);

	useEffect(() => {
		if (!modalOpen) {
			return;
		}

		const selectedUserWhatsappId = Number(selectedQueueUser?.whatsapp?.id);
		const selectedUserDefaultWhatsapp = filteredWhatsapps.find(
			whatsapp => Number(whatsapp.id) === selectedUserWhatsappId
		);

		const userDefaultWhatsapp = filteredWhatsapps.find(
			whatsapp => Number(whatsapp.id) === Number(user?.whatsappId)
		);

		const companyDefaultWhatsapp = filteredWhatsapps.find(
			whatsapp => Boolean(whatsapp.isDefault)
		);

		const fallbackWhatsapp =
			selectedUserDefaultWhatsapp ||
			queueDefaultWhatsapp ||
			companyDefaultWhatsapp ||
			userDefaultWhatsapp ||
			(filteredWhatsapps.length === 1 ? filteredWhatsapps[0] : null);

		if (
			selectedWhatsapp &&
			filteredWhatsapps.some(
				whatsapp => Number(whatsapp.id) === Number(selectedWhatsapp)
			)
		) {
			return;
		}

		setSelectedWhatsapp(fallbackWhatsapp ? fallbackWhatsapp.id : "");
	}, [
		modalOpen,
		filteredWhatsapps,
		selectedWhatsapp,
		user?.whatsappId,
		queueDefaultWhatsapp,
		selectedQueueUser,
	]);

	useEffect(() => {
		if (!modalOpen || !selectedQueue) {
			setQueueUsers([]);
			setLoadingQueueUsers(false);
			return;
		}

		const fetchQueueUsers = async () => {
			setLoadingQueueUsers(true);
			try {
				const { data } = await api.get("/users/transfer-options", {
					params: {
						searchParam: "",
						queueId: selectedQueue,
					},
				});
				const fetchedQueueUsers = Array.isArray(data?.users) ? data.users : [];
				setQueueUsers(fetchedQueueUsers);

				setSelectedUser(prevSelectedUser => {
					if (
						prevSelectedUser &&
						fetchedQueueUsers.some(queueUser => String(queueUser.id) === String(prevSelectedUser))
					) {
						return prevSelectedUser;
					}

					const loggedUserInQueue = fetchedQueueUsers.find(
						queueUser => Number(queueUser.id) === Number(user?.id)
					);

					if (loggedUserInQueue) {
						return String(loggedUserInQueue.id);
					}

					if (fetchedQueueUsers.length === 1) {
						return String(fetchedQueueUsers[0].id);
					}

					return "";
				});
			} catch (err) {
				setQueueUsers([]);
				toastError(err);
			} finally {
				setLoadingQueueUsers(false);
			}
		};

		fetchQueueUsers();
	}, [modalOpen, selectedQueue, user?.id]);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContact(null);
		setSelectedWhatsapp("");
		setSelectedQueue("");
		setSelectedUser("");
		setQueueUsers([]);
	};

	const handleSaveTicket = async contactId => {
		if (!contactId || !selectedWhatsapp || !selectedQueue || !selectedUser) return;
		setLoading(true);
		try {
			const { data: ticket } = await api.post("/tickets", {
				contactId: contactId,
				userId: Number(selectedUser),
				queueId: Number(selectedQueue),
				status: "open",
				whatsappId: Number(selectedWhatsapp),
			});
			history.push(`/tickets/${ticket.id}`, { switchTab: "open" });
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
		handleClose();
	};

	const handleSelectOption = (e, newValue) => {
		if (newValue?.number) {
			setSelectedContact(newValue);
		} else if (newValue?.name) {
			setNewContact({ name: newValue.name });
			setContactModalOpen(true);
		}
	};

	const handleCloseContactModal = () => {
		setContactModalOpen(false);
	};

	const handleAddNewContactTicket = contact => {
		handleSaveTicket(contact.id);
	};

	const createAddContactOption = (filterOptions, params) => {
		const filtered = filter(filterOptions, params);

		if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
			filtered.push({
				name: `${params.inputValue}`,
			});
		}

		return filtered;
	};

	const renderOption = option => {
		if (option.number) {
			return `${option.name} - ${option.number}`;
		} else {
			return `${i18n.t("newTicketModal.add")} ${option.name}`;
		}
	};

	const renderOptionLabel = option => {
		if (option.number) {
			return `${option.name} - ${option.number}`;
		} else {
			return `${option.name}`;
		}
	};

	return (
		<>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
				onSave={handleAddNewContactTicket}
			></ContactModal>
			<Dialog open={modalOpen} onClose={handleClose} fullWidth maxWidth="xs">
				<DialogTitle id="form-dialog-title">
					{i18n.t("newTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers style={{ width: "100%", boxSizing: "border-box" }}>
					{initialContact?.id ? (
						<TextField
							fullWidth
							variant="outlined"
							margin="dense"
							size="small"
							label={i18n.t("newTicketModal.fieldLabel")}
							value={renderOptionLabel(initialContact)}
							InputProps={{ readOnly: true }}
						/>
					) : (
						<Autocomplete
							options={options}
							loading={loading}
							style={{ width: "100%" }}
							clearOnBlur
							autoHighlight
							freeSolo
							clearOnEscape
							getOptionLabel={renderOptionLabel}
							renderOption={renderOption}
							filterOptions={createAddContactOption}
							onChange={(e, newValue) => handleSelectOption(e, newValue)}
							renderInput={params => (
								<TextField
									{...params}
									label={i18n.t("newTicketModal.fieldLabel")}
									variant="outlined"
									margin="dense"
									size="small"
									autoFocus
									onChange={e => setSearchParam(e.target.value)}
									onKeyPress={e => {
										if (loading || loadingWhatsapps || !selectedContact || !selectedWhatsapp || !selectedQueue || !selectedUser) return;
										if (e.key === "Enter") {
											handleSaveTicket(selectedContact.id);
										}
									}}
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<React.Fragment>
												{loading ? (
													<CircularProgress color="inherit" size={20} />
												) : null}
												{params.InputProps.endAdornment}
											</React.Fragment>
										),
									}}
								/>
							)}
						/>
					)}
					<FormControl
							variant="outlined"
							margin="dense"
							fullWidth
							style={{ marginTop: 12 }}
							disabled={availableQueues.length === 0}
						>
							<InputLabel>{i18n.t("newTicketModal.queueLabel")}</InputLabel>
							<Select
								size="small"
								value={selectedQueue}
								onChange={e => {
									setSelectedQueue(e.target.value);
									setSelectedUser("");
									setSelectedWhatsapp("");
								}}
								label={i18n.t("newTicketModal.queueLabel")}
							>
								<MenuItem value="" disabled>
									{availableQueues.length
										? i18n.t("newTicketModal.queuePlaceholder")
										: i18n.t("newTicketModal.noQueueUsers")}
								</MenuItem>
								{availableQueues.map(queue => (
									<MenuItem key={queue.id} value={String(queue.id)}>
										{queue.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl
							variant="outlined"
							margin="dense"
							fullWidth
							style={{ marginTop: 12 }}
							disabled={!selectedQueue || loadingQueueUsers}
						>
							<InputLabel>{i18n.t("newTicketModal.userLabel")}</InputLabel>
							<Select
								size="small"
								value={selectedUser}
								onChange={e => setSelectedUser(e.target.value)}
								label={i18n.t("newTicketModal.userLabel")}
							>
								<MenuItem value="" disabled>
									{loadingQueueUsers
										? i18n.t("newTicketModal.loadingUsers")
										: i18n.t("newTicketModal.userPlaceholder")}
								</MenuItem>
								{queueUsers.map(queueUser => (
									<MenuItem key={queueUser.id} value={String(queueUser.id)}>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												width: "100%",
												gap: 8,
											}}
										>
											<span>{queueUser.name}</span>
											<span
												style={{
													width: 16,
													height: 16,
													borderRadius: "50%",
													backgroundColor: queueUser?.isOnline ? "#2e7d32" : "#d32f2f",
													display: "inline-block",
													flexShrink: 0,
												}}
											/>
										</div>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					<FormControl
						variant="outlined"
						margin="dense"
						fullWidth
						style={{ marginTop: 12 }}
						disabled={loadingWhatsapps || filteredWhatsapps.length === 0}
					>
						<InputLabel>{i18n.t("newTicketModal.connectionLabel")}</InputLabel>
						<Select
							size="small"
							value={selectedWhatsapp}
							onChange={e => setSelectedWhatsapp(e.target.value)}
							label={i18n.t("newTicketModal.connectionLabel")}
						>
							<MenuItem value="" disabled>
								{loadingWhatsapps
									? i18n.t("newTicketModal.loadingConnections")
									: filteredWhatsapps.length
										? i18n.t("newTicketModal.connectionPlaceholder")
										: i18n.t("newTicketModal.noConnections")}
							</MenuItem>
							{filteredWhatsapps.map(whatsapp => (
								<MenuItem key={whatsapp.id} value={whatsapp.id}>
									{whatsapp.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading}
						variant="outlined"
						className={classes.actionButton}
					>
						{i18n.t("newTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="button"
						className={classes.actionButton}
						disabled={!selectedContact || !selectedWhatsapp || !selectedQueue || !selectedUser || loadingWhatsapps || loadingQueueUsers}
						onClick={() => handleSaveTicket(selectedContact.id)}
						color="primary"
						loading={loading}
					>
						{i18n.t("newTicketModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewTicketModal;
